// src/pages/auth/RoleProvisioningPage.tsx
//
// Replaces the old "What's your role?" picker. Self-signup is always a Clinic
// Admin (staff/doctors are added later from inside the workspace), so instead
// of asking, we auto-assign the Admin role and continue to onboarding.
//
// This still routes through OnboardingAPI.setRole so the backend side effects
// are preserved: Cognito custom:role sync, authoritative users.role write,
// TOS/Privacy consent logging, and the audit-log entry.
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentUser, signOut } from "aws-amplify/auth";
import AuthLayout from "../../layouts/AuthLayout";
import { OnboardingAPI, OnboardingApiError } from "../../services/onboarding.service";
import { ONBOARDING_STATUS_ROUTES, TOS_VERSION, PP_VERSION } from "../../constants";

const RoleProvisioningPage: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const provision = useCallback(async () => {
    setError(null);

    // Step 1: Must be signed in — bounce to /login if not.
    try {
      await getCurrentUser();
    } catch {
      navigate("/login", { replace: true });
      return;
    }

    // Step 2: If a role is already set (e.g. user hit this URL directly after
    // onboarding), skip provisioning and continue to the right destination.
    // A 404/401/403 (except device MFA) means the user row isn't there yet —
    // fall through and let setRole create it.
    try {
      const { role } = await OnboardingAPI.getMe();
      if (role === "PlatformAdmin") {
        navigate("/platform/kyc", { replace: true });
        return;
      }
      if (role) {
        try {
          const { onboarding_status, clinic_id } = await OnboardingAPI.getStatus();
          // A non-empty role here doesn't mean Admin was ever actually
          // assigned — device-check's auto-create defaults a brand-new
          // signup to "Doctor" before this page gets a chance to run (see
          // useRedirectIfAuthenticated's matching fix). Only treat the role
          // as already-decided once the clinic exists (role is frozen
          // server-side at that point) or it's genuinely Admin already —
          // otherwise fall through to Step 3 below and self-assign Admin,
          // same as a fresh signup would.
          if (role === "Admin" || clinic_id) {
            if (onboarding_status === "completed") {
              navigate("/dashboard", { replace: true });
            } else {
              navigate(
                ONBOARDING_STATUS_ROUTES[onboarding_status] ?? "/onboarding/clinic-details",
                { replace: true },
              );
            }
            return;
          }
        } catch {
          navigate("/onboarding/clinic-details", { replace: true });
          return;
        }
      }
    } catch (err) {
      const status = err instanceof OnboardingApiError ? err.statusCode : 0;
      const message = err instanceof OnboardingApiError ? err.message : "";

      // Device MFA must never look like a "workspace setup" failure — send
      // the user back to login where LoginPage shows the OTP step.
      if (status === 403 && message === "device_verification_required") {
        navigate("/login", { replace: true });
        return;
      }

      if (status !== 404 && status !== 401 && status !== 403) {
        setError("Unable to reach the server. Please check your connection and try again.");
        return;
      }
      // 404/401/403 → no user row yet; proceed to auto-assign Admin.
    }

    // Step 3: Auto-assign Admin (idempotent server-side) and continue.
    try {
      await OnboardingAPI.setRole("Admin");
      // Fire-and-forget: enrich the consent log with device details so the
      // backend can send the consent confirmation email. Never blocks navigation.
      void OnboardingAPI.recordConsentDetails({
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        method: "email_signup",
        client_tos_version: TOS_VERSION,
        client_pp_version: PP_VERSION,
      }).catch(() => {});
      navigate("/onboarding/clinic-details", { replace: true });
    } catch (err: any) {
      const message = err?.message || "";
      if (err?.statusCode === 403 && message === "device_verification_required") {
        navigate("/login", { replace: true });
        return;
      }
      setError(message || "Unable to finish setting up your account. Please try again.");
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    if (!cancelled) provision();
    return () => {
      cancelled = true;
    };
  }, [provision]);

  const handleSignOut = async () => {
    await signOut().catch(() => {});
    navigate("/login", { replace: true });
  };

  if (error) {
    return (
      <AuthLayout
        title="Setting up your workspace"
        subtitle="We hit a snag preparing your account. You can retry or sign out."
        heroTitleLine1="Your workspace,"
        heroTitleLine2="almost ready"
      >
        <div className="space-y-4">
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
            {error}
          </p>
          <button
            type="button"
            onClick={provision}
            className="w-full rounded-2xl border-2 border-[#0066ff] bg-white p-4 text-base font-semibold text-[#0066ff] transition-all duration-200 hover:bg-blue-50/40 hover:shadow-md"
          >
            Try again
          </button>
          <p className="pt-2 text-sm text-slate-500">
            Wrong account?{" "}
            <button
              type="button"
              onClick={handleSignOut}
              className="font-medium text-[#0066cc] hover:underline"
            >
              Sign out
            </button>
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-sm text-slate-400">Setting up your workspace…</div>
    </div>
  );
};

export default RoleProvisioningPage;
